import { useState, useEffect, useRef } from "react";
import { Gift, ChevronRight, ChevronLeft, Loader2, Check, ExternalLink, Star, ShoppingBag } from "lucide-react";
import { getGiftDataByRecipientEmail, getWishItems, type Wishlist, type WishItem } from "@/services/wishlists";
import { updateWishItem } from "@/services/wishlists";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import BlurImage from "./BlurImage";
import WishlistBottomSheet from "./WishlistBottomSheet";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  recipientEmail: string;
  recipientName: string;
}

const RecipientWishlistsInline = ({ recipientEmail, recipientName }: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const [itemsByList, setItemsByList] = useState<Record<string, WishItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [openSheetList, setOpenSheetList] = useState<Wishlist | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!recipientEmail) { setLoading(false); return; }
    let active = true;

    (async () => {
      const { data } = await getGiftDataByRecipientEmail(recipientEmail);
      if (!active) return;

      const allLists = data?.wishlists?.filter((w) => w.type === "wishlist" || w.type === "registry") || [];

      if (allLists.length > 0) {
        const results = await Promise.all(allLists.map((w) => getWishItems(w.id)));
        if (!active) return;
        const map: Record<string, WishItem[]> = {};
        allLists.forEach((w, i) => { map[w.id] = results[i].data || []; });
        // Only keep lists that have at least one item
        const nonEmpty = allLists.filter((w) => (map[w.id]?.length ?? 0) > 0);
        setItemsByList(map);
        setWishlists(nonEmpty);
        if (nonEmpty.length > 0) setActiveListId(nonEmpty[0].id);
      } else {
        setWishlists([]);
      }

      setLoading(false);
    })();

    return () => { active = false; };
  }, [recipientEmail]);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    checkScroll();
  }, [activeListId, itemsByList]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -160 : 160, behavior: "smooth" });
  };

  const handleClaim = async (itemId: string) => {
    if (!user) return;
    const displayName = user.user_metadata?.display_name || user.email || "Someone";
    const { error } = await updateWishItem(itemId, {
      is_claimed: true,
      claimed_by_name: displayName,
      claimed_at: new Date().toISOString(),
    });
    if (!error) {
      setItemsByList((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].map((i) =>
            i.id === itemId
              ? { ...i, is_claimed: true, claimed_by_name: displayName, claimed_at: new Date().toISOString() }
              : i
          );
        }
        return next;
      });
      toast({ title: "Claimed!", description: "This wish has been marked as yours." });
    }
  };

  if (loading) {
    return (
      <div className="w-full rounded-2xl bg-card border border-border p-6 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Loading wishlists…</span>
      </div>
    );
  }

  if (wishlists.length === 0) return null;

  const activeItems = activeListId ? (itemsByList[activeListId] || []) : [];
  const activeList = wishlists.find((w) => w.id === activeListId);

  return (
    <div className="w-full space-y-4">
      {/* Section header — editorial */}
      <div className="flex items-end justify-between gap-3">
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 font-medium">
            Gift Ideas
          </p>
          <p className="text-sm font-serif italic text-foreground/85 leading-snug">
            A few things {recipientName} would love
          </p>
        </div>

        {/* Scroll arrows — desktop */}
        <div className="hidden sm:flex items-center gap-1 shrink-0">
          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
            className="p-1.5 rounded-full border border-border bg-background hover:bg-muted disabled:opacity-30 transition-all"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            aria-label="Scroll right"
            className="p-1.5 rounded-full border border-border bg-background hover:bg-muted disabled:opacity-30 transition-all"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* List tabs — refined, outlined active state */}
      {wishlists.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
          {wishlists.map((wl) => {
            const items = itemsByList[wl.id] || [];
            const unclaimedCount = items.filter((i) => !i.is_claimed).length;
            const isActive = activeListId === wl.id;

            return (
              <button
                key={wl.id}
                onClick={() => setActiveListId(wl.id)}
                className={`shrink-0 flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-medium tracking-wide transition-all border ${
                  isActive
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-foreground/70 border-border hover:border-foreground/40 hover:text-foreground"
                }`}
              >
                {wl.type === "registry" ? <ShoppingBag size={11} /> : <Gift size={11} />}
                <span>{wl.title}</span>
                {unclaimedCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold ${
                    isActive ? "bg-background/20 text-background" : "bg-muted text-foreground/70"
                  }`}>
                    {unclaimedCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Carousel */}
      <AnimatePresence mode="wait">
        {activeListId && activeItems.length > 0 && (
          <motion.div
            key={activeListId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            <div>
              <div
                ref={scrollRef}
                onScroll={checkScroll}
                className="overflow-x-auto pb-0 scrollbar-hide snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                <div className="flex gap-2.5 pr-1">
                  {activeItems.map((item) => (
                    <div key={item.id} className="shrink-0">
                      <CarouselProductCard
                        item={item}
                        isAuthenticated={!!user}
                        onClaim={handleClaim}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* View full list — opens bottom sheet */}
            {activeList && (
              <button
                type="button"
                onClick={() => setOpenSheetList(activeList)}
                className="group/link mx-auto flex items-center gap-2 text-[11px] font-medium tracking-[0.12em] uppercase text-foreground/70 hover:text-foreground transition-colors py-2 w-fit"
              >
                <span className="h-px w-6 bg-foreground/30 group-hover/link:bg-foreground/60 transition-colors" />
                View full list
                <ChevronRight size={12} className="transition-transform group-hover/link:translate-x-0.5" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {openSheetList && (
        <WishlistBottomSheet
          wishlist={openSheetList}
          onClose={() => setOpenSheetList(null)}
        />
      )}
    </div>
  );
};

/** Premium compact product card for horizontal carousel */
function CarouselProductCard({
  item,
  isAuthenticated,
  onClaim,
}: {
  item: WishItem;
  isAuthenticated: boolean;
  onClaim: (id: string) => void;
}) {
  const isClaimed = !!item.is_claimed;

  return (
    <div
      className={`shrink-0 w-[148px] snap-start rounded-[20px] overflow-hidden group bg-card transition-all ${
        isClaimed
          ? "border border-border/40 opacity-55"
          : "border border-border/60 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:border-foreground/15"
      }`}
    >
      {/* Image — object-contain so nothing is cropped */}
      <div className="relative w-full aspect-square bg-muted/30 overflow-hidden flex items-center justify-center p-3">
        {item.image_url ? (
          <BlurImage
            src={item.image_url}
            alt={item.title}
            className={`max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105 ${isClaimed ? "grayscale" : ""}`}
          />
        ) : (
          <Gift size={28} className="text-muted-foreground/20" />
        )}

        {/* Top pick badge */}
        {item.is_most_desired && !isClaimed && (
          <div className="absolute top-2 left-2 bg-foreground text-background rounded-full px-2 py-0.5 flex items-center gap-1 shadow-sm">
            <Star size={8} fill="currentColor" />
            <span className="text-[9px] font-bold uppercase tracking-wider">Top</span>
          </div>
        )}

        {/* External link icon */}
        {item.link_url && !isClaimed && (
          <a
            href={item.link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open product link"
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/90 backdrop-blur-sm border border-border/60 flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-background transition-all"
          >
            <ExternalLink size={11} />
          </a>
        )}

        {/* Claimed overlay */}
        {isClaimed && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] flex items-center justify-center">
            <div className="bg-foreground text-background rounded-full px-3 py-1 flex items-center gap-1">
              <Check size={10} strokeWidth={3} />
              <span className="text-[9px] font-bold uppercase tracking-wider">Claimed</span>
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-3 pb-3 pt-2 space-y-1.5">
        <p className="text-[12px] font-semibold text-foreground line-clamp-2 leading-snug min-h-[2.4em]">
          {item.title}
        </p>

        {item.price != null && (
          <p className="text-[12px] font-bold text-foreground tracking-tight">
            {item.price} <span className="text-[10px] font-medium text-muted-foreground">AED</span>
          </p>
        )}

        {/* Claim button */}
        {!isClaimed && isAuthenticated && (
          <button
            onClick={() => onClaim(item.id)}
            className="w-full text-[10px] font-bold uppercase tracking-[0.1em] text-background bg-foreground hover:bg-foreground/90 rounded-full py-1.5 transition-all"
          >
            I'll get this
          </button>
        )}

        {isClaimed && item.claimed_by_name && (
          <p className="text-[9px] text-muted-foreground truncate italic">
            By {item.claimed_by_name}
          </p>
        )}
      </div>
    </div>
  );
}

export default RecipientWishlistsInline;
